import { useState } from "react";
import { Link } from "@/lib/router-compat";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Navbar } from "@/components/public/Navbar";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { usePageSEO } from "@/hooks/usePageSEO";
import { format } from "date-fns";
import { Search, Clock, Eye, Sparkles } from "lucide-react";
import { CURATED_GUIDES } from "@/data/curatedGuides";
import { getBlogCoverImage, DEFAULT_MUMBAI_FALLBACK } from "@/utils/blogImages";

export default function Blog() {
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  usePageSEO({
    title: "Mumbai Real Estate Guides, Locality Insights & Market Data | MNRE",
    description: "Data-driven guides and locality intelligence for Mumbai property buyers and investors. Price per sq ft, rental yields, legal costs, and project reviews.",
    canonicalPath: "/blog",
  });

  const { data: posts, isLoading } = useQuery({
    queryKey: ["blog-posts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("id, title, slug, excerpt, cover_image_url, category, tags, published_at, author_name, views_count")
        .eq("is_published", true)
        .order("published_at", { ascending: false });
      
      const dbPosts = data || [];
      const dbPostsMap = new Map(dbPosts.map((p) => [p.slug, p]));

      // Curated guides match up with genuine backend views from database
      const curatedSummaries = CURATED_GUIDES.map((g) => {
        const dbEntry = dbPostsMap.get(g.slug);
        return {
          id: dbEntry?.id || g.id,
          title: g.title,
          slug: g.slug,
          excerpt: g.excerpt,
          cover_image_url: g.cover_image_url,
          category: g.category,
          tags: g.tags,
          published_at: g.published_at,
          author_name: g.author_name,
          views_count: dbEntry?.views_count ?? 0,
        };
      });

      // Combine curated + database, deduplicate by slug, then sort by published_at descending
      const seen = new Set<string>();
      const combined: any[] = [];
      for (const p of [...curatedSummaries, ...dbPosts]) {
        if (!seen.has(p.slug)) {
          seen.add(p.slug);
          combined.push(p);
        }
      }

      // Sort all posts chronologically — newest first
      combined.sort((a, b) => {
        const dateA = a.published_at ? new Date(a.published_at).getTime() : 0;
        const dateB = b.published_at ? new Date(b.published_at).getTime() : 0;
        return dateB - dateA;
      });

      return combined;
    },
  });

  const categories = posts ? [...new Set(posts.map((p) => p.category).filter(Boolean))] : [];

  const filtered = posts?.filter((p) => {
    const matchesSearch = !search || p.title.toLowerCase().includes(search.toLowerCase()) || p.excerpt?.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = !selectedCategory || p.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <section className="bg-gradient-to-br from-primary/10 via-background to-secondary/10 py-16 border-b">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <h1 className="text-3xl md:text-5xl font-bold font-playfair italic mb-4">Real Estate Blog</h1>
          <p className="text-muted-foreground text-lg mb-8">Market insights, buying guides & expert advice for Mumbai property.</p>
          <div className="relative max-w-md mx-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search articles…" className="pl-10" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-10">
        {categories.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8">
            <Badge variant={selectedCategory === null ? "default" : "outline"} className="cursor-pointer" onClick={() => setSelectedCategory(null)}>All</Badge>
            {categories.map((cat) => (
              <Badge key={cat} variant={selectedCategory === cat ? "default" : "outline"} className="cursor-pointer capitalize" onClick={() => setSelectedCategory(cat as string)}>
                {cat}
              </Badge>
            ))}
          </div>
        )}

        {isLoading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <Card key={i}><CardContent className="p-0"><Skeleton className="h-48 w-full rounded-t-lg" /><div className="p-4 space-y-2"><Skeleton className="h-5 w-3/4" /><Skeleton className="h-4 w-full" /></div></CardContent></Card>
            ))}
          </div>
        ) : filtered && filtered.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((post) => {
              const coverUrl = getBlogCoverImage(post);
              return (
                <Link key={post.id} to={`/blog/${post.slug}`}>
                  <Card className="overflow-hidden hover:shadow-lg transition-all duration-300 h-full flex flex-col group border-border/60 hover:border-primary/40">
                    <div className="h-52 w-full bg-muted overflow-hidden relative">
                      <img
                        src={coverUrl}
                        alt={post.title}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                        decoding="async"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = DEFAULT_MUMBAI_FALLBACK;
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/10 pointer-events-none" />
                      {post.category && (
                        <span className="absolute top-3 left-3 bg-black/60 backdrop-blur-md text-white text-xs px-2.5 py-1 rounded-full border border-white/20 font-medium capitalize shadow-sm">
                          {post.category}
                        </span>
                      )}
                    </div>
                    <CardContent className="p-5 flex-1 flex flex-col justify-between">
                      <div>
                        <h2 className="font-playfair font-semibold text-lg mb-2 line-clamp-2 group-hover:text-primary transition-colors">{post.title}</h2>
                        {post.excerpt && <p className="text-sm text-muted-foreground line-clamp-3 mb-3 leading-relaxed">{post.excerpt}</p>}
                      </div>
                      <div className="flex items-center justify-between text-xs text-muted-foreground pt-3 border-t">
                        {post.published_at && (
                          <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{format(new Date(post.published_at), "MMM d, yyyy")}</span>
                        )}
                        {typeof post.views_count === "number" ? (
                          <span className="flex items-center gap-1">
                            <Eye className="h-3 w-3" />
                            {post.views_count === 1 ? "1 view" : `${post.views_count} views`}
                          </span>
                        ) : null}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-16 text-muted-foreground">
            <p className="text-lg">No articles found.</p>
            <p className="text-sm mt-1">Check back soon for new content!</p>
          </div>
        )}
      </section>
    </div>
  );
}
